const ET_ZONE = 'America/New_York';
const PKT_ZONE = 'Asia/Karachi';

// Intl.DateTimeFormat construction is expensive; build each (locale, options) formatter once.
const formatterCache = new Map();

function getFormatter(locale, options) {
  const key = `${locale}|${JSON.stringify(options)}`;
  let formatter = formatterCache.get(key);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat(locale, options);
    formatterCache.set(key, formatter);
  }
  return formatter;
}

const DISPLAY_OPTIONS = {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
};
const ET_DISPLAY_OPTIONS = { timeZone: ET_ZONE, ...DISPLAY_OPTIONS };
const PKT_DISPLAY_OPTIONS = { timeZone: PKT_ZONE, ...DISPLAY_OPTIONS };
const ET_DATE_KEY_OPTIONS = {
  timeZone: ET_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
};

/** Calendar date in Eastern Time as YYYY-MM-DD, offset by whole days. */
export function etDateKey(dayOffset = 0) {
  const parts = getFormatter('en-CA', ET_DATE_KEY_OPTIONS).format(new Date());
  const base = new Date(`${parts}T00:00:00Z`);
  base.setUTCDate(base.getUTCDate() + dayOffset);
  return base.toISOString().slice(0, 10);
}
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function normalizeWall(value) {
  if (!value) return '';
  return String(value).trim().slice(0, 16);
}

function parseWall(value) {
  const wall = normalizeWall(value);
  if (!wall.includes('T')) return null;
  const [datePart, timePart] = wall.split('T');
  const [year, month, day] = datePart.split('-').map(Number);
  const [hour, minute] = timePart.split(':').map(Number);
  if (!year || !month || !day || Number.isNaN(hour) || Number.isNaN(minute)) return null;
  return { year, month, day, hour, minute };
}

/** Format a stored ET wall-clock value exactly as entered (no browser timezone). */
export function formatWallTimeET(wall) {
  const parts = parseWall(wall);
  if (!parts) return '—';
  const { year, month, day, hour, minute } = parts;
  const period = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 || 12;
  return `${MONTHS[month - 1]} ${day}, ${year}, ${hour12}:${String(minute).padStart(2, '0')} ${period} ET`;
}

const zonePartsFormatters = new Map();

function partsInZone(date, zone) {
  let formatter = zonePartsFormatters.get(zone);
  if (!formatter) {
    formatter = getFormatter('en-US', {
      timeZone: zone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
    zonePartsFormatters.set(zone, formatter);
  }
  const parts = formatter.formatToParts(date);
  const get = (type) => parts.find((p) => p.type === type)?.value;
  return {
    year: Number(get('year')),
    month: Number(get('month')),
    day: Number(get('day')),
    hour: Number(get('hour')),
    minute: Number(get('minute')),
  };
}

/** ET wall-clock → UTC ISO (for sorting / PKT conversion only). */
export function etWallToUTC(wall) {
  const parts = parseWall(wall);
  if (!parts) return '';
  const { year, month, day, hour, minute } = parts;
  const target = { year, month, day, hour, minute };

  let guess = Date.UTC(year, month - 1, day, hour + 4, minute);
  for (let i = 0; i < 48; i += 1) {
    const current = partsInZone(new Date(guess), ET_ZONE);
    if (
      current.year === target.year
      && current.month === target.month
      && current.day === target.day
      && current.hour === target.hour
      && current.minute === target.minute
    ) {
      return new Date(guess).toISOString();
    }
    const diffMinutes = (
      (target.day - current.day) * 24 * 60
      + (target.hour - current.hour) * 60
      + (target.minute - current.minute)
    );
    guess += diffMinutes * 60 * 1000;
  }
  return '';
}

/** PKT display derived from the ET wall-clock the user entered. */
export function formatWallTimePKT(wall) {
  const utc = etWallToUTC(wall);
  if (!utc) return '—';
  const formatted = getFormatter('en-US', PKT_DISPLAY_OPTIONS).format(new Date(utc));
  return `${formatted} PKT`;
}

/** Form input value from stored ET wall-clock or legacy UTC ISO. */
export function toETInputValue(meeting) {
  if (meeting?.scheduled_at_et) return normalizeWall(meeting.scheduled_at_et);
  const iso = meeting?.scheduled_at;
  if (!iso) return '';
  const utc = parseUTC(iso);
  if (!utc) return '';
  const p = partsInZone(utc, ET_ZONE);
  const pad = (n) => String(n).padStart(2, '0');
  return `${p.year}-${pad(p.month)}-${pad(p.day)}T${pad(p.hour)}:${pad(p.minute)}`;
}

export function parseUTC(iso) {
  if (!iso) return null;
  const raw = String(iso).trim();
  const normalized = raw.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(raw)
    ? raw
    : `${raw}Z`;
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatMeetingTimeET(isoOrMeeting) {
  if (typeof isoOrMeeting === 'object' && isoOrMeeting?.scheduled_at_et) {
    return formatWallTimeET(isoOrMeeting.scheduled_at_et);
  }
  const wall = typeof isoOrMeeting === 'string' && isoOrMeeting.includes('T') && !isoOrMeeting.endsWith('Z')
    ? isoOrMeeting
    : null;
  if (wall && wall.length === 16) return formatWallTimeET(wall);
  const utc = parseUTC(isoOrMeeting);
  if (!utc) return '—';
  const formatted = getFormatter('en-US', ET_DISPLAY_OPTIONS).format(utc);
  return `${formatted} ET`;
}

export function formatMeetingTimePKT(isoOrMeeting) {
  if (typeof isoOrMeeting === 'object' && isoOrMeeting?.scheduled_at_et) {
    return formatWallTimePKT(isoOrMeeting.scheduled_at_et);
  }
  const utc = parseUTC(typeof isoOrMeeting === 'string' ? isoOrMeeting : isoOrMeeting?.scheduled_at);
  if (!utc) return '—';
  const formatted = getFormatter('en-US', PKT_DISPLAY_OPTIONS).format(utc);
  return `${formatted} PKT`;
}

/** @deprecated use scheduled_at_et string directly */
export function etInputToISO(localValue) {
  return etWallToUTC(localValue);
}
